import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { supportedLanguages, type SupportedLanguage } from "@/lib/i18n";
import { getPageFromSlug, generateLocalizedUrl, type PageSlug } from "@/lib/slugUtils";
import StructuredData from "./StructuredData";
import { useEffect } from "react";
import { siteUrl, geoTargeting } from "@/lib/siteConfig";
import { trackSEOEvents } from "@/lib/seoAnalytics";
interface SEOHeadProps {
  page?: 'home' | 'about' | 'services' | 'portfolio' | 'contact' | 'price-form' | 'jotform-expert' | 'fillout-expert' | 'industry-real-estate' | 'industry-ecommerce' | 'industry-restaurants' | 'industry-financial' | 'industry-agencies';
  title?: string;
  description?: string;
  lang?: SupportedLanguage;
  path?: string;
}

const SEOHead = ({ page, title, description, lang, path }: SEOHeadProps) => {
  const { t, i18n } = useTranslation(['seo', 'common'], { useSuspense: false });
  const location = useLocation();
  
  const currentLanguage = i18n.language as SupportedLanguage;
  const baseUrl = siteUrl;
  const canonicalHost = (() => { try { return new URL(baseUrl).host; } catch { return ''; } })();
  // Only block staging domains from indexing, not the non-www version of main domain
  const isStagingDomain = typeof window !== 'undefined' && !window.location.host.includes('priceforms.com');
  const robotsDefault = page === 'contact' ? 'index, follow, noarchive, max-image-preview:large' : 'index, follow, max-image-preview:large';
  const robotsContent = isStagingDomain ? 'noindex, nofollow, noarchive, nosnippet' : robotsDefault;
  
  // Generate hreflang URLs for all language versions of the current page
  const generateHrefLangs = () => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    
    if (pathSegments.length === 1) {
      // Home page
      return supportedLanguages.map(lang => ({
        hreflang: lang,
        href: `${baseUrl}/${lang}/`,
      }));
    }
    
    if (pathSegments.length === 2) {
      // Page with translated slug
      const [currentLang, currentSlug] = pathSegments;
      const currentPage = getPageFromSlug(currentSlug, currentLang);
      
      if (currentPage) {
        return supportedLanguages.map(lang => ({
          hreflang: lang,
          href: `${baseUrl}${generateLocalizedUrl(currentPage, lang)}`,
        }));
      }
    }
    
    // Fallback to home page for unrecognized paths
    return supportedLanguages.map(lang => ({
      hreflang: lang,
      href: `${baseUrl}/${lang}/`,
    }));
  };

  const hrefLangs = generateHrefLangs();
  
  // Generate canonical URL for current page
  const generateCanonicalUrl = () => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    
    if (pathSegments.length === 1) {
      return `${baseUrl}/${currentLanguage}/`;
    }
    
    if (pathSegments.length === 2) {
      const [currentLang, currentSlug] = pathSegments;
      const currentPage = getPageFromSlug(currentSlug, currentLang);
      
      if (currentPage) {
        return `${baseUrl}${generateLocalizedUrl(currentPage, currentLanguage)}`;
      }
    }
    
    return `${baseUrl}/${currentLanguage}/`;
  };

  const canonicalUrl = generateCanonicalUrl();
  
  // Cleanup pre-hydration SEO tags so Helmet controls head uniquely per page
  useEffect(() => {
    try {
      const nodes = document.querySelectorAll(
        'meta[name="description"][data-bootstrap="true"], link[rel="canonical"][data-bootstrap="true"], title[data-bootstrap="true"]'
      );
      nodes.forEach((n) => n.parentElement?.removeChild(n));
    } catch (e) {
      // noop
    }
  }, [location.pathname, i18n.language]);
  
  // Validate currentLanguage
  const validLanguage = supportedLanguages.includes(currentLanguage) ? currentLanguage : 'en';

  // Analytics: Track page view
  useEffect(() => {
    trackSEOEvents.pageView(page, validLanguage);
  }, [location.pathname, validLanguage, page]);
  return (
    <>
      <Helmet>
        <title>{t(`seo:${page}.title`)}</title>
        <meta name="description" content={t(`seo:${page}.description`)} />
        
        
        {/* Language and location - Enhanced with stronger lang attribute setting */}
        <html lang={validLanguage} />
        <meta name="language" content={validLanguage} />
        <meta name="content-language" content={validLanguage} />
        <meta httpEquiv="content-language" content={validLanguage} />
        
        {/* Geographic targeting (only on localized pages) */}
        {geoTargeting[validLanguage] && (
          <>
            <meta name="geo.region" content={geoTargeting[validLanguage].region} />
            <meta name="geo.placename" content={geoTargeting[validLanguage].placename} />
          </>
        )}
        
        {/* Canonical URL */}
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Hreflang tags */}
        {hrefLangs.map(({ hreflang, href }) => (
          <link key={hreflang} rel="alternate" hrefLang={hreflang} href={href} />
        ))}
        {/* Add x-default for default language */}
        <link rel="alternate" hrefLang="x-default" href={`${baseUrl}/en/`} />
        
        {/* Open Graph */}
        <meta property="og:title" content={t(`seo:${page}.title`)} />
        <meta property="og:description" content={t(`seo:${page}.description`)} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Smart Price Forms Solutions" />
        <meta property="og:locale" content={validLanguage === 'en' ? 'en_US' : 
                                            validLanguage === 'da' ? 'da_DK' :
                                            validLanguage === 'sk' ? 'sk_SK' : 'cs_CZ'} />
        <meta property="og:image" content={`${baseUrl}/og-image-${validLanguage}.png?v=20251004`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={t(`seo:${page}.title`)} />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:updated_time" content={new Date().toISOString()} />
        {/* Alternate locales */}
        {supportedLanguages.filter(lang => lang !== validLanguage).map(lang => (
          <meta key={lang} property="og:locale:alternate" content={
            lang === 'en' ? 'en_US' : 
            lang === 'da' ? 'da_DK' :
            lang === 'sk' ? 'sk_SK' : 'cs_CZ'
          } />
        ))}
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t(`seo:${page}.title`)} />
        <meta name="twitter:description" content={t(`seo:${page}.description`)} />
        <meta name="twitter:image" content={`${baseUrl}/og-image-${validLanguage}.png?v=20251004`} />
        <meta name="twitter:image:alt" content={t(`seo:${page}.title`)} />
        <meta name="twitter:creator" content="@danielgadus" />
        <meta name="twitter:site" content="@priceforms" />
        
        {/* Additional SEO */}
        <meta name="robots" content={robotsContent} />
        {isStagingDomain && <meta name="googlebot" content="noindex, nofollow, noarchive, nosnippet" />}
        <meta name="author" content={t('common:author')} />
        <meta name="generator" content="React + Vite" />
        <meta name="keywords" content={t(`seo:${page}.keywords`, '')} />
        <meta name="theme-color" content="#3b82f6" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="format-detection" content="telephone=no" />
        
        {/* Social media verification - Add your codes from Google Search Console & Bing Webmaster Tools */}
        <meta name="google-site-verification" content="" />
        <meta name="msvalidate.01" content="" />
        
        {/* Performance hints */}
        <link rel="dns-prefetch" href="//fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.googleapis.com" crossOrigin="anonymous" />
        
      </Helmet>
      
      <StructuredData page={page} currentLanguage={validLanguage} />
    </>
  );
};

export default SEOHead;